/**
 * Все проекты примерно одинаковые. В сетевых вызовах на проектах все должно быть одинаково
 * Если вы добавляете в проект какую-то логику формирования запроса - скорее всего она нужна всем или не нужна :)
 */
import { globalStore, pwaAtom } from '@alfa-bank/newclick-global';
import { AKEY_UA_SUBSTR, isAkeyUA, isAkeyWebview, isVoskhodUA, VOSKHOD_UA_SUBSTR, } from '@alfa-bank/newclick-helpers/utils/akey/is-akey-webview';
import { bffBaseUrlPath } from '../../constants/bff-base-url-path';
import { DEVICE_APP_ID, PC_USER_ID } from '../../constants/cookies';
import { CSRF_COOKIE_NAME } from '../../constants/csrf-cookie-name';
import { AdditionalHeaders } from '../../constants/headers';
import { getConfig } from '../../utils/client-config';
import { getPlatformType } from '../../utils/get-platform-type';
import { getBridgeToNative } from '../../window-utils';
import { getClientTimezone } from './get-client-timezone';
import { getOs } from './get-os';
import { getRandomTraceId } from './get-random-trace-id';
import { setHeaderFromCookie } from './set-header-from-cookie';
const PWA_UA_SUBSTR = 'PWA';
/**
 * Добавляет заголовок `Channel-ID` ко всем запросам.
 * Нужен для всех апишек мобайла.
 * По умолчанию проставляется на newclick-cerberus-web.
 */
export const channelId = (config, param = {}) => ({
    ...config,
    headers: {
        ...config.headers,
        [AdditionalHeaders.ChannelId]: param.channelId || '',
    },
});
/**
 * Добавляет базовый url ко всем запросам
 * @param baseURL Base url для апи
 */
export function baseUrl(baseURL) {
    const configExtender = (config) => ({
        ...config,
        baseURL,
    });
    return configExtender;
}
/**
 * Добавляет базовый url к bff запросам
 */
export function bffbaseUrl() {
    return baseUrl(`${getConfig()?.servicePath}/${bffBaseUrlPath}`);
}
/**
 * Добавляет идентификатор пользователя в заголовки.
 *
 * @param config - Конфиг, который расширится перед отправкой на сервер.
 * @param param - Параметры, которые будут переданы в вызов сервиса.
 *
 * @prop {string} customerId - Идентификатор пользователя.
 */
export const customerId = (config, param = {}) => ({
    ...config,
    headers: {
        ...config.headers,
        [AdditionalHeaders.Cus]: param.customerId || '',
    },
});
/**
 * Добавляет заголовок `host` ко всем запросам.
 * Нужен для правильной маршрутизации запроса внутри кластера
 */
export const host = (config, param = {}) => ({
    ...config,
    headers: {
        ...config.headers,
        ...(process.env.PRESERVE_HOST_HEADER === 'true' && {
            [AdditionalHeaders.Host]: param.host || '',
        }),
    },
});
/**
 * Устанавливает XB3-trace-id заголовок
 */
export const xb3TraceIdServer = (config, { traceId } = {}) => ({
    ...config,
    headers: {
        ...config.headers,
        [AdditionalHeaders.TraceId]: traceId || getRandomTraceId(),
        [AdditionalHeaders.SpanId]: getRandomTraceId(),
    },
});
/**
 * Генерирует X-B3-TraceId и добавляет в заголовки
 */
export const xb3TraceId = (config) => ({
    ...config,
    headers: {
        ...config.headers,
        [AdditionalHeaders.TraceId]: getRandomTraceId(),
        [AdditionalHeaders.SpanId]: getRandomTraceId(),
    },
});
/**
 * Добавляет AO-ORIGIN-APPLICATION-ID заголовок к запросу
 * @param appId applicationId приложения с которого уходит запрос
 */
export function originApplicationId(appId) {
    const configExtender = (config) => ({
        ...config,
        headers: {
            ...config.headers,
            [AdditionalHeaders.OriginApplicationId]: appId,
        },
    });
    return configExtender;
}
/**
 * Добавляет DEVICE-APP-ID заголовок к запросу
 */
export const addDeviceAppIdHeader = (config) => setHeaderFromCookie({
    ...config,
    xHeaderName: AdditionalHeaders.DeviceAppId,
    xCookieName: DEVICE_APP_ID,
});
/**
 * Добавляет ApplicationId заголовок к запросу
 */
export const hostWebviewApplicationId = (config) => {
    if (typeof window === 'undefined') {
        return config;
    }
    const { userAgent } = window.navigator;
    const pwa = globalStore.get(pwaAtom);
    if (pwa) {
        return {
            ...config,
            headers: {
                ...config.headers,
                [AdditionalHeaders.HostWebviewApplicationId]: PWA_UA_SUBSTR,
            },
        };
    }
    if (isAkeyWebview(userAgent)) {
        let headerValue;
        if (isAkeyUA(userAgent)) {
            headerValue = AKEY_UA_SUBSTR;
        }
        else if (isVoskhodUA(userAgent)) {
            headerValue = VOSKHOD_UA_SUBSTR;
        }
        return {
            ...config,
            headers: {
                ...config.headers,
                [AdditionalHeaders.HostWebviewApplicationId]: headerValue,
            },
        };
    }
    return config;
};
export function checkErrors(response) {
    if (response.status < 200 || response.status >= 300) {
        // eslint-disable-next-line no-console
        console.error(response.statusText);
    }
    if (response.status === 204 || response.status === 205) {
        return null;
    }
    return response.data;
}
/**
 * Устанавливает заголовок `X-CSRF-Token`
 */
export const xcsrfToken = (config) => ({
    ...config,
    xsrfCookieName: CSRF_COOKIE_NAME,
    xsrfHeaderName: AdditionalHeaders.Xsrf,
});
/**
 * Устанавливает заголовок `X-CSRF-Token`
 */
export const addPcUserIdHeader = (config) => setHeaderFromCookie({
    ...config,
    xCookieName: PC_USER_ID,
    xHeaderName: AdditionalHeaders.PcUserId,
});
/**
 * Устанавливает параметр `withCredentials`, необходимо для кросс-доменных запросов
 */
export const withCredentials = (config) => ({
    ...config,
    withCredentials: true,
});
/**
 * Добавляет заголовок с ролью клиента
 */
export const userRole = (config, param = {}) => ({
    ...config,
    headers: {
        ...config.headers,
        ...(param.userRole && { [AdditionalHeaders.UserRole]: param.userRole }),
    },
});
/**
 * Добавляет заголовок с типом идентификации клиента
 */
export const identificationType = (config, param = {}) => ({
    ...config,
    headers: {
        ...config.headers,
        ...(param.identificationType && {
            [AdditionalHeaders.IdentificationType]: param.identificationType,
        }),
    },
});
/**
 * Добавляет заголовок authPlasticId
 */
export const authPlasticId = (config, param = {}) => ({
    ...config,
    headers: {
        ...config.headers,
        ...(param.authPlasticId && { [AdditionalHeaders.AuthPlasticId]: param.authPlasticId }),
    },
});
/**
 * Добавляет заголовок cardOwnerId
 */
export const cardOwnerId = (config, param = {}) => ({
    ...config,
    headers: {
        ...config.headers,
        ...(param.cardOwnerId && { [AdditionalHeaders.CardOwnerId]: param.cardOwnerId }),
    },
});
/**
 * Генерирует часовой пояс и добавляет в заголовки
 */
export const timezoneOffset = (config) => ({
    ...config,
    headers: {
        ...config.headers,
        [AdditionalHeaders.Timezone]: getClientTimezone(),
    },
});
/**
 * Проброс заголовка X-FEATURE-NAME для тестовой среды
 */
export const featureName = (config, param = {}) => {
    const shouldAddHeader = param.featureName && process.env.NODE_ENV !== 'production';
    return {
        ...config,
        headers: {
            ...config.headers,
            ...(shouldAddHeader && { [AdditionalHeaders.XfeatureName]: param.featureName }),
        },
    };
};
/**
 * Добавляет заголовок `X-WEBVIEW` для признака запроса из АМ-вебью окружения
 */
export const signWebview = (config) => {
    const bridgeToNative = getBridgeToNative();
    return {
        ...config,
        headers: {
            ...config.headers,
            ...(bridgeToNative && { [AdditionalHeaders.Xwebview]: 'AM' }),
        },
    };
};
/**
 * Добавляет заголовок `OS`.
 * Содержит тип операционой системы AM для мидловых сервисов.
 */
export const os = (config, param = {}) => {
    const osEnvironment = getOs(param.userAgent) || '';
    return {
        ...config,
        headers: {
            ...config.headers,
            ...(osEnvironment && { [AdditionalHeaders.Os]: osEnvironment }),
        },
    };
};
/**
 * Добавляет заголовок `app-version` для новых вебвью.
 * Содержит версию приложения AM для мидловых сервисов
 */
export const appVersion = (config) => {
    const bridgeToNative = getBridgeToNative();
    const bridgeAppVersion = bridgeToNative?.appVersion || '';
    return {
        ...config,
        headers: {
            ...config.headers,
            ...(bridgeToNative && { [AdditionalHeaders.AppVersion]: bridgeAppVersion }),
        },
    };
};
/**
 * Добавляет заголовок `X-SCREEN-DIMENSION`
 */
export const addXScreenDimensionHeader = (config) => {
    if (typeof window === 'undefined') {
        return config;
    }
    const platformType = getPlatformType();
    return {
        ...config,
        headers: {
            ...config.headers,
            [AdditionalHeaders.XscreenDimension]: platformType,
        },
    };
};
